import React, { useMemo } from 'react';
import {
  ContentfulArticle,
  ContentfulCompetition,
  ContentfulEntry,
  ContentfulGenericPage,
  EntryType,
} from '@jbhi-fi/apis/src/contentful/types';
import { MiniCardGroup, MiniCardGroupProps } from '../../MiniCardGroup/MiniCardGroup';
import { MiniCardProps } from '../../MiniCard/MiniCard';
import { ContentfulFilter } from '../../hooks/useContentfulEntries/useContentfulEntries';
import { CardTransformer } from '../transformers/CardTransformer';
import { useCardEntries } from '../../hooks/useCardEntries';
import { sortEntriesByPublishedOrCreatedDateDesc } from '../../hooks/useCards';

type ContentfulMiniCardEntry = ContentfulArticle | ContentfulCompetition | ContentfulGenericPage;
export type MiniCardGroupRendererProps = MiniCardGroupProps & {
  contentfulFilters?: Array<ContentfulFilter>;
  hideHeadingAndLink?: boolean;
};

const DEFAULT_FETCH_COUNT = 3;

const isMiniCardCompatibleEntry = (entry: ContentfulEntry<unknown>): entry is ContentfulMiniCardEntry => {
  const contentType = entry?.sys?.contentType?.sys?.id;
  return (
    contentType === EntryType.article || contentType === EntryType.competition || contentType === EntryType.genericPage
  );
};

export const mergeMiniCards = ({
  explicitCards,
  fetchedCards,
}: {
  explicitCards: Array<MiniCardProps>;
  fetchedCards: Array<MiniCardProps>;
}): Array<MiniCardProps> => {
  const seen = new Set<string>();
  return explicitCards.concat(fetchedCards).filter(card => {
    const key = card.destinationUrl || card.title;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const MiniCardGroupRenderer: React.FC<MiniCardGroupRendererProps> = ({
  cards: explicitCards,
  linkProps,
  contentfulFilters = [],
  hideHeadingAndLink = false,
  ...props
}) => {
  const explicitDisplayCards = useMemo(
    () => mergeMiniCards({ explicitCards, fetchedCards: [] }).slice(0, DEFAULT_FETCH_COUNT),
    [explicitCards]
  );
  const shouldFetchCards = contentfulFilters.length > 0 && explicitDisplayCards.length < DEFAULT_FETCH_COUNT;
  const { entries: fetchedEntries } = useCardEntries({
    filters: contentfulFilters,
    page: 1,
    // Fetch a full set of candidates so duplicates against explicit cards can still backfill up to 3 cards.
    pageSize: shouldFetchCards ? DEFAULT_FETCH_COUNT : 0,
    appendResults: false,
    enabled: shouldFetchCards,
    logPrefix: 'MiniCardGroupRenderer',
  });
  const fetchedCards = useMemo(
    () =>
      fetchedEntries
        .filter(isMiniCardCompatibleEntry)
        .sort(sortEntriesByPublishedOrCreatedDateDesc)
        .map(CardTransformer.toMiniCardProps)
        .filter(Boolean),
    [fetchedEntries]
  );
  const cards = useMemo(
    () => mergeMiniCards({ explicitCards: explicitDisplayCards, fetchedCards }).slice(0, DEFAULT_FETCH_COUNT),
    [explicitDisplayCards, fetchedCards]
  );

  return (
    <MiniCardGroup
      {...props}
      cards={cards}
      heading={hideHeadingAndLink ? '' : undefined}
      linkProps={hideHeadingAndLink ? undefined : linkProps}
    />
  );
};
